import { motion } from "motion/react";
import { AlertTriangle, TrendingDown, Eye, DollarSign } from "lucide-react";
import { useEffect, useState } from "react";

export function BlackHoleSection() {
  const [absorbedData, setAbsorbedData] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setAbsorbedData((prev) => (prev + 1) % 100);
    }, 100);
    return () => clearInterval(interval);
  }, []);

  const problems = [
    {
      icon: Eye,
      title: "Cegueira Analítica",
      description: "73% dos seus visitantes são invisíveis para GA4 e pixels tradicionais",
      impact: "R$ 50k-200k/mês perdidos",
      color: "#FF4D00"
    },
    {
      icon: TrendingDown,
      title: "Fragmentação de Dados",
      description: "Suas ferramentas não conversam entre si. Resultado: decisões às cegas",
      impact: "40% ROI perdido",
      color: "#00F5FF"
    },
    {
      icon: DollarSign,
      title: "Carrinhos Fantasmas",
      description: "Você vê que abandonaram, mas não sabe QUEM abandonou nem POR QUÊ",
      impact: "30% de receita recuperável",
      color: "#10B981"
    }
  ];

  return (
    <section className="relative py-32 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0B0E14] via-[#0B0E14]/95 to-[#0B0E14]" />
      
      {/* Grid pattern */}
      <div className="absolute inset-0 opacity-10">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(to right, #FF4D00 1px, transparent 1px),
              linear-gradient(to bottom, #FF4D00 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px'
          }}
        />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#FF4D00]/30 bg-[#FF4D00]/5 mb-6">
            <AlertTriangle className="w-4 h-4 text-[#FF4D00]" />
            <span className="text-sm text-[#FF4D00] font-mono">CRITICAL ALERT</span>
          </div>
          
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-6">
            O Buraco Negro dos
            <span className="block text-[#FF4D00]">Dados Perdidos</span>
          </h2>
          
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Enquanto você lê isso, <span className="text-[#FF4D00] font-bold">milhares de reais</span> estão sendo sugados 
            pelo vácuo da ignorância analítica
          </p>
        </motion.div>

        {/* Black Hole Visual */}
        <div className="relative mb-20">
          <div className="relative w-full max-w-[600px] mx-auto aspect-square">
            {/* Outer glow */}
            <motion.div
              className="absolute inset-0 rounded-full"
              style={{
                background: 'radial-gradient(circle, rgba(255,77,0,0.3) 0%, rgba(255,77,0,0.1) 40%, transparent 70%)',
                filter: 'blur(40px)'
              }}
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
            />

            {/* Accretion disk rings */}
            {[1, 2, 3, 4, 5].map((ring) => (
              <motion.div
                key={ring}
                className="absolute inset-0 flex items-center justify-center"
                style={{ rotate: ring * 30 }}
                animate={{ rotate: 360 }}
                transition={{
                  duration: 20 - ring * 2,
                  repeat: Infinity,
                  ease: 'linear'
                }}
              >
                <div
                  className="rounded-full border opacity-30"
                  style={{
                    width: `${ring * 18}%`,
                    height: `${ring * 18}%`,
                    borderColor: ring % 2 === 0 ? '#FF4D00' : '#00F5FF',
                    borderWidth: '2px',
                    borderStyle: 'dashed'
                  }}
                />
              </motion.div>
            ))}

            {/* Data particles being absorbed */}
            {Array.from({ length: 30 }).map((_, idx) => {
              const angle = (idx / 30) * 360;
              const radius = 250;
              return (
                <motion.div
                  key={idx}
                  className="absolute w-2 h-2 rounded-full bg-[#00F5FF]"
                  style={{
                    left: '50%',
                    top: '50%'
                  }}
                  animate={{
                    x: [
                      Math.cos((angle * Math.PI) / 180) * radius,
                      Math.cos((angle * Math.PI) / 180) * 20
                    ],
                    y: [
                      Math.sin((angle * Math.PI) / 180) * radius,
                      Math.sin((angle * Math.PI) / 180) * 20
                    ],
                    opacity: [1, 0],
                    scale: [1, 0]
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    delay: idx * 0.1,
                    ease: 'easeIn'
                  }}
                />
              );
            })}

            {/* Event horizon (center) */}
            <motion.div
              className="absolute inset-0 flex items-center justify-center"
              animate={{ rotate: 360 }}
              transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
            >
              <div className="w-32 h-32 rounded-full bg-gradient-to-br from-[#FF4D00] via-black to-black border-4 border-[#FF4D00]/50 shadow-2xl shadow-[#FF4D00]/50" />
            </motion.div>

            {/* Data counter in center */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <div className="text-sm text-[#FF4D00] font-mono mb-1">DADOS PERDIDOS</div>
                <div className="text-3xl font-bold text-white font-mono">
                  {absorbedData}%
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Problem Cards */}
        <div className="grid md:grid-cols-3 gap-8">
          {problems.map((problem, idx) => {
            const Icon = problem.icon;
            return (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                whileHover={{ y: -10, scale: 1.02 }}
                className="relative group"
              >
                {/* Glow effect */}
                <div
                  className="absolute -inset-0.5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-xl"
                  style={{ backgroundColor: problem.color }}
                />
                
                {/* Card content */}
                <div className="relative p-8 rounded-xl border bg-gradient-to-br from-[#0B0E14] to-[#0B0E14]/50 backdrop-blur-sm transition-all duration-300"
                  style={{ borderColor: `${problem.color}30` }}
                >
                  {/* Icon */}
                  <div
                    className="w-14 h-14 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform"
                    style={{ 
                      backgroundColor: `${problem.color}15`,
                      border: `2px solid ${problem.color}40`
                    }}
                  >
                    <Icon className="w-7 h-7" style={{ color: problem.color }} />
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl font-bold text-white mb-3">
                    {problem.title}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-300 mb-6 leading-relaxed">
                    {problem.description}
                  </p>

                  {/* Impact badge */}
                  <div
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold"
                    style={{
                      backgroundColor: `${problem.color}15`,
                      color: problem.color,
                      border: `1px solid ${problem.color}40`
                    }}
                  >
                    <AlertTriangle className="w-4 h-4" />
                    {problem.impact}
                  </div>

                  {/* Scan line effect */}
                  <motion.div
                    className="absolute inset-0 pointer-events-none rounded-xl overflow-hidden"
                    initial={{ opacity: 0 }}
                    whileHover={{ opacity: 1 }}
                  >
                    <motion.div
                      className="absolute inset-x-0 h-px"
                      style={{ backgroundColor: problem.color, opacity: 0.5 }}
                      animate={{ y: [0, 300] }}
                      transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                    />
                  </motion.div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-16"
        >
          <p className="text-xl text-gray-300 mb-8">
            A pergunta não é <span className="text-[#FF4D00] font-bold">SE</span> você está perdendo dinheiro.
            <br />
            É <span className="text-[#00F5FF] font-bold">QUANTO</span> você está perdendo.
          </p>
          <button className="px-10 py-5 bg-[#FF4D00] hover:bg-[#FF4D00]/90 text-white text-lg rounded-lg font-bold transition-all hover:scale-105 hover:shadow-lg hover:shadow-[#FF4D00]/50">
            Pare de Perder Receita Agora
          </button>
        </motion.div>
      </div>
    </section>
  );
}
